/**
 * Headless domain helpers for the readiness axis (`WorkUnitSummaryItem.readiness`
 * — see api-types.ts). Readiness answers "how far through pre-production is
 * this job" and is deliberately independent of RAG/deadline urgency, which
 * answers "is anything wrong with it". Label text only, no rendering, no
 * styling — the visual variant (colour) per value is a shell concern; see
 * apps/frontend's `issueBadges.tsx` for the same split, and
 * `ReadinessBadge.tsx` for this axis's shell half.
 */
import type { Readiness } from "../data/api-types";

/** Ladder order, upstream to downstream — not the array order the backend
 *  sends (`job_count` DESC). Consumers rendering all readiness values in a
 *  pipeline (the readiness strip) should sort by this order for display. */
export const READINESS_LADDER: readonly Readiness[] = [
  "not_production_ready",
  "out_for_proof",
  "production_ready",
  "in_production",
  "non_press",
];

export const READINESS_LABELS: Record<
  Exclude<Readiness, "non_press">,
  string
> = {
  not_production_ready: "Not production ready",
  out_for_proof: "Out for proof",
  production_ready: "Production ready",
  in_production: "In production",
};

/** Label for a readiness badge. `non_press` has no ladder label of its own
 *  — non-press work (Factored/Postage/Merchandise/…) will never reach the
 *  print-readiness ladder, so callers show the job's own `work_kind` text
 *  instead of a readiness badge that would never apply. */
export function readinessLabel(readiness: Readiness): string | null {
  if (readiness === "non_press") return null;
  return READINESS_LABELS[readiness];
}

/** Label for any readiness value with no null case — for callers (the
 *  readiness strip, the jobs-page readiness chip) that show one string per
 *  value and have no job-specific `work_kind` on hand to show instead.
 *  `non_press` falls back to the generic "Non-press work" wording. A caller
 *  that *does* have the job's own `work_kind` (e.g. `ReadinessBadge`) should
 *  prefer that over this fallback and call `readinessLabel` directly. */
export function readinessDisplayLabel(readiness: Readiness): string {
  return readinessLabel(readiness) ?? "Non-press work";
}
